import { entriesFor } from "./intake";
import type { FoodEntry, FoodItem, KayaState, MealSlot } from "./types";

/**
 * The food log as a CSV file (board YouAccountExport): one row per entry, every nutrient the entry
 * copied when it was logged, for a doctor, a dietitian or a spreadsheet of one's own.
 *
 * A value Kaya does not know is an empty cell, never 0 (invariant 18) — a blank reads as "not
 * recorded" in any spreadsheet, where a 0 would be summed as a fact.
 */

/** Where an entry's figures came from, by the table row it was logged from (`FoodItem.src`). */
export type SourceOf = (code: string) => FoodItem["src"] | undefined;

export const FOOD_CSV_HEADER = [
  "Date",
  "Time",
  "Meal",
  "Food",
  "Portion",
  "Grams",
  "kcal",
  "Protein (g)",
  "Carbohydrate (g)",
  "Fat (g)",
  "Fibre (g)",
  "Saturated fat (g)",
  "Sodium (mg)",
  "Calcium (mg)",
  "Iron (mg)",
  "Potassium (mg)",
  "Source",
] as const;

/** Singular, unlike the Food tab's "Snacks" heading: each row is one thing eaten. */
const MEAL_WORD: Record<MealSlot, string> = {
  breakfast: "Breakfast",
  lunch: "Lunch",
  dinner: "Dinner",
  snack: "Snack",
};

/** The words the file uses for `FoodItem.src`; an INDB dish is an estimate (home versions vary). */
const SOURCE_WORD: Record<NonNullable<FoodItem["src"]>, string> = {
  u: "table",
  // FNDDS and Foundation Foods rows are measured tables too (ADR-037)
  f: "table",
  o: "table",
  l: "label",
  i: "estimated dish",
  c: "yours",
};

/** The dates with at least one live entry, oldest first. */
function loggedDates(S: KayaState): string[] {
  return Object.keys(S.logs)
    .filter((ds) => entriesFor(S, ds).length > 0)
    .sort();
}

/** "371 entries on 58 days since 1 Jul" — what the page says the file will hold. */
export function foodLogSummary(S: KayaState): { entries: number; days: number; first: string | null } {
  const dates = loggedDates(S);
  const entries = dates.reduce((n, ds) => n + entriesFor(S, ds).length, 0);
  return { entries, days: dates.length, first: dates[0] ?? null };
}

export function foodLogFileName(today: string): string {
  return `kaya-food-log-${today}.csv`;
}

/** A number as a cell: absent or not finite is blank, never 0. */
function num(v: number | undefined, digits: number): string {
  if (typeof v !== "number" || !Number.isFinite(v)) return "";
  const f = 10 ** digits;
  return String(Math.round(v * f) / f);
}

/**
 * RFC 4180 quoting, plus a guard for text a spreadsheet would run: a food name the user typed as
 * "=…" or "+…" is a formula to Excel and Sheets, so it is prefixed with an apostrophe and stays text.
 */
function cell(s: string): string {
  const safe = /^[=+\-@\t\r]/.test(s) ? `'${s}` : s;
  return /[",\r\n]/.test(safe) || safe !== safe.trim() ? `"${safe.replace(/"/g, '""')}"` : safe;
}

function clock(at: number): string {
  if (!Number.isFinite(at) || at <= 0) return "";
  const d = new Date(at);
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

function portion(e: FoodEntry): string {
  const oil = e.oilTsp ? ` + ${num(e.oilTsp, 1)} tsp oil` : "";
  return `${num(e.qty, 2)} ${e.unit}${oil}`;
}

function source(e: FoodEntry, srcOf: SourceOf): string {
  // No code is a line typed by hand; `cf:` is one of the user's own foods, even one since deleted.
  if (!e.code || e.code.startsWith("cf:")) return SOURCE_WORD.c;
  const src = srcOf(e.code);
  return src ? SOURCE_WORD[src] : "";
}

function row(ds: string, e: FoodEntry, srcOf: SourceOf): string {
  return [
    ds,
    clock(e.at),
    MEAL_WORD[e.meal] ?? "",
    e.name,
    portion(e),
    num(e.grams, 1),
    num(e.kcal, 0),
    num(e.p, 1),
    num(e.c, 1),
    num(e.f, 1),
    num(e.fb, 1),
    num(e.sf, 1),
    num(e.na, 0),
    num(e.ca, 0),
    num(e.fe, 1),
    num(e.pot, 0),
    source(e, srcOf),
  ]
    .map(cell)
    .join(",");
}

/**
 * The whole log, oldest day first and each day in the order it was eaten. Removed entries are
 * left out — the file is what the Food tab shows, not the merge's bookkeeping. CRLF line ends, as
 * RFC 4180 has them and Excel expects.
 */
export function foodLogCsv(S: KayaState, srcOf: SourceOf): string {
  const lines = [FOOD_CSV_HEADER.map(cell).join(",")];
  for (const ds of loggedDates(S)) for (const e of entriesFor(S, ds)) lines.push(row(ds, e, srcOf));
  return lines.join("\r\n") + "\r\n";
}
